//-- Copyright (c) 2021 TMT Insights Group, LLC
import { AllTableColumnsConfig } from '@tmt-insights/oms_fe_core';
import { IngestFile } from '@/redux/slices/slice_types';
import { Grid, Typography } from '@tmt-insights/oms_fe_core/material';
import { formatBytes } from '@tmt-insights/oms_fe_core/utils';
import {
  ActionableTableStatus,
  DateTimeDisplay,
  EllipsisTooltip,
  FileTypeDisplay,
  IngestManagementOverflowMenu,
  IngestManagementRowExpand,
  PolarisIcon
} from '@tmt-insights/oms_fe_core/columns';

const columns: AllTableColumnsConfig<IngestFile> = {
  expandable: {
    label: '',
    isExpandable: true,
    render: (row: IngestFile, frozenRightWidth: number) => <IngestManagementRowExpand data={row} previewWidth={frozenRightWidth} />
  },
  hid: {
    label: 'ID',
    sortField: 'id',
    position: 'left',
    width: 100,
    render: (cell: IngestFile['hid']) => <strong style={{ color: 'var(--primary-main)' }}>{cell}</strong>
  },
  created_at: {
    label: 'Created',
    position: 'left',
    sortField: 'created_at',
    render: (cell: IngestFile['created_at']) => <DateTimeDisplay>{cell}</DateTimeDisplay>
  },
  version_serialized__name: {
    label: 'Edit Class',
    minWidth: 300,
    disableSort: true,
    render: (cell: NonNullable<IngestFile['version_serialized']>['name']) => <EllipsisTooltip text={cell} maxWidth={280} />
  },
  version_serialized__lineage_meta__series_name: {
    label: 'Series Name',
    minWidth: 300,
    disableSort: true
  },
  version_serialized__lineage_meta__season_sequence_order: {
    label: 'Season #',
    minWidth: 100,
    disableSort: true
  },
  version_serialized__lineage_meta__episode_name: {
    label: 'Episode Name',
    minWidth: 350,
    disableSort: true
  },
  version_serialized__rightsline_id: {
    label: 'Rightsline ID',
    minWidth: 200,
    disableSort: true
  },
  filename: {
    label: 'File Name',
    minWidth: 400,
    render: (cell: IngestFile['filename']) => {
      return cell ? (
        <Grid container alignItems="center">
          <PolarisIcon icon="file" height={20} style={{ marginRight: 5, opacity: 0.6 }} />
          <EllipsisTooltip text={cell} maxWidth={340} />
        </Grid>
      ) : (
        <Typography sx={{ opacity: 0.5, fontSize: '1rem', display: 'inline-flex', alignItems: 'center' }}>
          <PolarisIcon icon="info" height={15} /> <em style={{ marginLeft: '5px' }}>Awaiting Filename Information</em>
        </Typography>
      );
    }
  },
  file_type: {
    label: 'File Type',
    disableSort: true,
    minWidth: 150,
    render: (cell: IngestFile['file_type']) => <FileTypeDisplay type={cell} />
  },
  file_size: {
    label: 'File Size',
    disableSort: true,
    render: (cell: IngestFile['file_size']) => {
      return cell ? (
        <Grid container alignItems="center">
          <PolarisIcon icon="clarity:hard-disk-solid" height={20} style={{ marginRight: 5, opacity: 0.6 }} />
          {formatBytes(cell)}
        </Grid>
      ) : (
        '-'
      );
    }
  },
  version_id: {
    label: 'Version ID',
    defaultDisabled: true,
    disableSort: true,
    render: (cell: IngestFile['version_id']) => {
      return cell ? cell : '-';
    }
  },
  stage: {
    label: 'Status/Stage',
    width: 250,
    render: (cell: string, row: IngestFile) => <ActionableTableStatus status={cell} row={row} />,
    position: 'right',
    disableSort: false
  },
  overflow: {
    label: '',
    position: 'right',
    width: 135,
    disableSort: true,
    render: (cell: IngestFile, row: IngestFile) => <IngestManagementOverflowMenu data={row} />
  }
};

export default columns;
